import React, { useMemo, useState } from 'react';
import { 
  ComposableMap, 
  Geographies, 
  Geography, 
  ZoomableGroup 
} from 'react-simple-maps';
import { Tooltip as AntTooltip, Button, Space } from 'antd';
import { PlusOutlined, MinusOutlined, AimOutlined } from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';

// World map topojson URL
const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

// Mapping from topojson country names to the dashboard's country abbreviations
const topoToDataName: Record<string, string> = {
  "United States of America": "USA",
  "United Kingdom": "UK",
  "South Korea": "South Korea",
  "China": "China",
  "Japan": "Japan",
  "Germany": "Germany",
  "France": "France",
  "Italy": "Italy",
  "Spain": "Spain",
  "Canada": "Canada",
  "Australia": "Australia",
  "Brazil": "Brazil",
  "India": "India",
  "Russia": "Russia",
  "Turkey": "Turkey",
  "Switzerland": "Switzerland",
  "Saudi Arabia": "Saudi Arabia",
  "Portugal": "Portugal",
  "Poland": "Poland",
  "Taiwan": "Taiwan",
  "South Africa": "South Africa",
  "Netherlands": "Netherlands",
  "Belgium": "Belgium",
  "Sweden": "Sweden",
  "Norway": "Norway",
  "Denmark": "Denmark",
  "Finland": "Finland",
  "Austria": "Austria",
  "Greece": "Greece",
  "Mexico": "Mexico",
  "Argentina": "Argentina",
  "Colombia": "Colombia",
  "Chile": "Chile",
  "Peru": "Peru",
  "Venezuela": "Venezuela",
  "Ecuador": "Ecuador",
  "Egypt": "Egypt",
  "Nigeria": "Nigeria",
  "Kenya": "Kenya",
  "Algeria": "Algeria",
  "Morocco": "Morocco",
  "Israel": "Israel",
  "Iran": "Iran",
  "Iraq": "Iraq",
  "Thailand": "Thailand",
  "Indonesia": "Indonesia",
  "Malaysia": "Malaysia",
  "Philippines": "Philippines",
  "Vietnam": "Vietnam",
  "Pakistan": "Pakistan",
  "Bangladesh": "Bangladesh",
  "New Zealand": "New Zealand",
  "Ireland": "Ireland",
  "Czech Republic": "Czech Republic",
  "Czechia": "Czech Republic",
  "Hungary": "Hungary",
  "Romania": "Romania",
  "Ukraine": "Ukraine",
};

// Helper function to format revenue values
const formatRevenue = (value: number) => {
  if (value === 0) return '$0.0M';
  if (Math.abs(value) < 1.0) {
    return `$${value.toFixed(2)}M`;
  }
  return `$${value.toFixed(1)}M`;
};

interface CountryWorldMapProps {
  countryData: Array<{
    country: string;
    revEnd: number;
    shareEnd: number;
    [key: string]: any;
  }>;
  colorMap: Record<string, string>;
  rowColor?: string; // Color for RoW / unmatched countries
}

const CountryWorldMap: React.FC<CountryWorldMapProps> = ({ 
  countryData, 
  colorMap,
  rowColor
}) => {
  const { isDark } = useTheme();
  const [position, setPosition] = useState({ coordinates: [10, -12], zoom: 0.83 });
  const [isDragging, setIsDragging] = useState(false);

  // Build a set of country names that have data
  const dataCountrySet = useMemo(() => {
    const set = new Set<string>();
    countryData.forEach(c => {
      if (c.country !== 'RoW' && c.country !== 'Others') {
        set.add(c.country);
      }
    });
    return set;
  }, [countryData]);

  // Build a lookup from data country name -> data
  const countryDataMap = useMemo(() => {
    const map = new Map<string, typeof countryData[0]>();
    countryData.forEach(c => map.set(c.country, c));
    return map;
  }, [countryData]);

  // Get the RoW entry
  const rowEntry = useMemo(() => countryData.find(c => c.country === 'RoW'), [countryData]);
  
  // Determine RoW fill color
  const rowFillColor = rowColor || colorMap['RoW'] || '#f5222d';

  const handleZoomIn = () => {
    if (position.zoom >= 6) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom * 1.2 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 0.8) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom / 1.2 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [10, -12], zoom: 0.83 });
  };

  const handleMoveEnd = (pos: any) => {
    setPosition(pos);
    setIsDragging(false);
  };

  const getFillColor = (topoCountryName: string): string => {
    const dataName = topoToDataName[topoCountryName] || topoCountryName;
    
    if (dataCountrySet.has(dataName)) {
      // Country has data - use its color from the color map
      return colorMap[dataName] || (isDark ? '#555' : '#bbb');
    }
    
    // Country does not have specific data - color as RoW (light grey)
    return isDark ? '#3a3a3a' : '#d9d9d9';
  };

  const mapStyles = useMemo(() => ({
    width: "100%",
    height: "100%",
    cursor: isDragging ? "grabbing" : "grab",
  }), [isDragging]);

  return (
    <div
      style={{ 
        width: '100%', 
        height: 'calc(100% - 20px)',
        minHeight: '300px',
        backgroundColor: isDark ? '#141414' : '#f9fafb', 
        borderRadius: '8px', 
        overflow: 'hidden',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        borderBottom: `2px solid ${isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'}`,
        marginBottom: '20px',
      }}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
    >
      {/* Zoom Controls */}
      <div style={{
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
      }}>
        <Space direction="vertical" size={4}>
          <Button 
            size="small"
            icon={<PlusOutlined />} 
            onClick={handleZoomIn} 
            disabled={position.zoom >= 6}
            title="Zoom In"
          />
          <Button 
            size="small"
            icon={<MinusOutlined />} 
            onClick={handleZoomOut} 
            disabled={position.zoom <= 0.8}
            title="Zoom Out"
          />
          <Button 
            size="small"
            icon={<AimOutlined />} 
            onClick={handleReset}
            title="Reset View"
          />
        </Space>
      </div>

      <ComposableMap
        projectionConfig={{
          scale: 210,
          center: [10, 0],
        }}
        style={mapStyles}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates as [number, number]}
          onMoveStart={() => setIsDragging(true)}
          onMoveEnd={handleMoveEnd}
          minZoom={0.5}
          maxZoom={10}
        >
          {/* Invisible background rect to capture drag events */}
          <rect
            x={-500}
            y={-500}
            width={2000}
            height={1500}
            fill="transparent"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          />
          <Geographies geography={geoUrl}>
            {({ geographies }: { geographies: any[] }) =>
              geographies
                .filter(geo => geo.properties.name !== "Antarctica")
                .map((geo: any) => {
                  const topoName = geo.properties.name;
                  const dataName = topoToDataName[topoName] || topoName;
                  const hasData = dataCountrySet.has(dataName);
                  const countryEntry = hasData ? countryDataMap.get(dataName) : null;
                  
                  const tooltipContent = (
                    <div style={{ padding: '2px' }}>
                      {hasData && countryEntry ? (
                        <div style={{ paddingBottom: '2px' }}>
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 600 }}>
                            {countryEntry.country}
                          </div>
                          <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
                            <div style={{ fontSize: '13px' }}>
                              Revenue: <span style={{ fontWeight: 700, color: '#fff' }}>{formatRevenue(countryEntry.revEnd)}</span>
                            </div>
                            <div style={{ fontSize: '13px' }}>
                              Market Share: <span style={{ fontWeight: 700, color: '#fff' }}>{countryEntry.shareEnd.toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ paddingBottom: '2px' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>{topoName}</div>
                          {rowEntry ? (
                            <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                                Part of RoW (Rest of World)
                              </div>
                              <div style={{ fontSize: '13px' }}>
                                RoW Revenue: <span style={{ fontWeight: 700, color: '#fff' }}>{formatRevenue(rowEntry.revEnd)}</span>
                              </div>
                              <div style={{ fontSize: '13px' }}>
                                RoW Market Share: <span style={{ fontWeight: 700, color: '#fff' }}>{rowEntry.shareEnd.toFixed(1)}%</span>
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '12px', fontStyle: 'italic', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                              No data available
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  
                  const fillColor = getFillColor(topoName);
                  
                  return (
                    <AntTooltip
                      key={geo.rsmKey}
                      title={tooltipContent}
                      mouseEnterDelay={0.05}
                      overlayStyle={{ pointerEvents: 'none' }}
                    >
                      <Geography
                        geography={geo}
                        fill={fillColor}
                        stroke={isDark ? "#141414" : "#ffffff"}
                        strokeWidth={0.5}
                        style={{
                          default: { outline: "none" },
                          hover: { 
                            fill: hasData 
                              ? `${fillColor}cc` 
                              : (isDark ? '#333' : '#ddd'),
                            outline: "none",
                            cursor: "pointer",
                            transition: "fill 0.2s ease"
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    </AntTooltip>
                  );
                })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
};

export default CountryWorldMap;
